<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import i18n from "../../../entrypoints/shared/i18n";
import HandRaiseVoter from "../shared/HandRaiseVoter.vue";
import gsap from "gsap";
import {
  orderVotersByTimestamp,
  orderOptionsByVotes,
  optionColorStyle,
  optionColorClass,
  optionTitle
} from "../../../entrypoints/shared/handraise_helpers";
import { flattenOptions } from "../../../entrypoints/shared/contest_utilities";

const { tc } = i18n.global;

const locale = computed(() => i18n.global.locale);

const store = useStore();

const votersByVote = computed(() => store.getters.handRaiseResultsByVoterVote);

const handRaiseLiveResults = computed(() => store.getters.handRaiseLiveResults);

// Only 1 contest per VR is enabled in handraise
const options = computed(() => flattenOptions(store.getters.contestsInActiveVotingRound[0].options));

const blankAccentColor = computed(() => store.getters.contestsInActiveVotingRound[0].blankOptionColor || null);

const onBeforeEnter = (el) => {
  el.style.opacity = 0;
  el.style.height = 0;
};

const onEnter = (el, done) => {
  gsap.to(el, {
    opacity: 1,
    height: "100%",
    delay: el.dataset.index * 0.15,
    onComplete: done,
  });
};

const onLeave = (el, done) => {
  gsap.to(el, {
    opacity: 0,
    height: 0,
    delay: el.dataset.index * 0.15,
    onComplete: done,
  });
};
</script>

<template>
  <div class="d-flex flex-column gap-3 mt-3">
    <TransitionGroup
      tag="div"
      :css="false"
      class="row-list d-flex flex-column gap-3"
      @before-enter="onBeforeEnter"
      @enter="onEnter"
      @leave="onLeave"
    >
      <div
        v-for="optionReference in orderOptionsByVotes(votersByVote)"
        :key="`${optionReference}-row`"
        class="dashboard-grid"
      >
        <div class="d-flex align-items-center gap-2">
          <div
            :class="`option-color-badge ${optionColorClass(optionReference, blankAccentColor)}`"
            :style="optionColorStyle(options, optionReference, blankAccentColor)"
          />
          <span class="text-truncate">
            {{ optionTitle(options, optionReference, locale) }}
          </span>
        </div>

        <span
          class="text-nowrap fw-bold"
          :class="{
            expanded: !handRaiseLiveResults[optionReference].results.percentage,
            'text-center': !handRaiseLiveResults[optionReference].results.percentage,
            'text-end': handRaiseLiveResults[optionReference].results.percentage,
          }"
        >
          {{ tc("js.handraise.list_view.votes", handRaiseLiveResults[optionReference].results.count) }}
        </span>

        <span v-if="handRaiseLiveResults[optionReference].results.percentage" class="text-nowrap fw-bold text-end">
          {{ handRaiseLiveResults[optionReference].results.percentage }}%
        </span>

        <TransitionGroup
          tag="ul"
          :css="false"
          class="voter-list"
          @before-enter="onBeforeEnter"
          @enter="onEnter"
          @leave="onLeave"
        >
          <li
            v-for="(voter, index) in orderVotersByTimestamp(votersByVote[optionReference].votes)"
            :key="voter.identifier"
            :data-index="index"
          >
            <HandRaiseVoter
              :voter="voter"
              :observer-mode="true"
              class="hand-raise-voter"
            />
          </li>
        </TransitionGroup>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.dashboard-grid {
  display: grid; 
  grid-auto-flow: column; 
  grid-template-columns: 10rem 4rem 4rem 1fr; 
  grid-template-rows: 2.75rem; 
  gap: 1rem 1rem;
  align-items: center;
}

.voter-list {
  padding: 0;
  margin-bottom: 0;
  overflow: hidden;
  list-style-type: none;
  display: flex;
  gap: 1rem;
  position: relative;
}

.row-list {
  padding: 0;
  margin-bottom: 0;
}

.voter-list::after {
  content: "";
  position: absolute;
  right: 0;
  width: 6rem;
  height: 100%;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%);
}

.hand-raise-voter {
  width: 10rem;
}

.expanded {
  grid-column: 2 / -2;
}
</style>
